import { RIS_BASE_URL } from 'config';
import { useAuth } from 'utils/auth';

export type MfaMethod = {
  id: string;
  type: string;
};

export const MFA_METHOD_DISPLAY_NAMES: Record<string, string> = {
  totp: 'One-time Password',
  sms: 'Phone Message',
};

export type MfaEnrollmentTicket = {
  ticketUrl: string;
};

type ApiEnvelope<T> = {
  data: T;
  errors: { code: string; message: string }[];
};

const request = async <T>(
  path: string,
  token: string,
  init: RequestInit = {}
): Promise<T> => {
  const response = await fetch(`${RIS_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw new Error(
      `MFA request to ${path} failed with status ${response.status}`
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const envelope: ApiEnvelope<T> = await response.json();
  return envelope.data;
};

const getMfaMethods = (token: string) =>
  request<MfaMethod[]>('/api/v1/users/my/mfa', token);

const enrollMfa = (token: string) =>
  request<MfaEnrollmentTicket>('/api/v1/users/my/mfa/enroll', token, {
    method: 'POST',
    body: '{}',
  });

const deleteMfaMethod = (authenticationMethodId: string, token: string) =>
  request<void>(
    `/api/v1/users/my/mfa/${encodeURIComponent(authenticationMethodId)}`,
    token,
    {
      method: 'DELETE',
    }
  );

const resetMfa = (token: string) =>
  request<void>('/api/v1/users/my/mfa', token, { method: 'DELETE' });

export const useMfaService = () => {
  const { getAccessTokenSilently } = useAuth();

  return {
    getMfaMethods: async () => getMfaMethods(await getAccessTokenSilently()),
    enrollMfa: async () => enrollMfa(await getAccessTokenSilently()),
    deleteMfaMethod: async (authenticationMethodId: string) =>
      deleteMfaMethod(authenticationMethodId, await getAccessTokenSilently()),
    resetMfa: async () => resetMfa(await getAccessTokenSilently()),
  };
};
